import {VALIDATORS} from '@github-ui/entity-validators'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLBox, VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {Checkbox, CheckboxGroup, FormControl} from '@primer/react'
import React, {useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {GraphqlFragment, IssueFormElementRef, SharedElementProps} from '../types'
import {safeGetDefaultValue} from '../utils/default-value'
import {prefixMarkdownCheckbox, prefixMarkdownTitle} from '../utils/markdown-conversion'
import {uniqueIdForElementInternal} from '../utils/session-storage'
import type {
  CheckboxesElement_input$data,
  CheckboxesElement_input$key,
} from './__generated__/CheckboxesElement_input.graphql'
import styles from './CheckboxesElement.module.css'

/**
 * Extracts default values from defaultValuesById and converts them to a boolean array.
 * Returns an array of booleans representing checkbox states, with proper length validation.
 */
export function getValidDefaultChoices({
  itemId,
  indexOfFormElement,
  defaultValuesById,
  checkboxCount,
}: {
  itemId: string | null | undefined
  indexOfFormElement?: number
  defaultValuesById: SharedElementProps['defaultValuesById']
  checkboxCount: number
}): boolean[] {
  if (!itemId) return Array(checkboxCount).fill(false)

  const defaultValues = safeGetDefaultValue({id: itemId, value: '', indexOfFormElement, defaultValuesById})
    .split(',')
    .map(str => str.trim() === 'true')

  // only use default values if one was provided for every checkbox
  return defaultValues.length === checkboxCount ? defaultValues : Array(checkboxCount).fill(false)
}

export type CheckboxesElementInternalProps = GraphqlFragment<CheckboxesElement_input$data> & {
  type: 'checkboxes'
  index?: number
}

export const CheckboxesElement = ({
  ref,
  elementRef,
  index,
  sessionStorageKey,
  defaultValuesById,
  onFieldChange,
}: {
  elementRef: CheckboxesElement_input$key
  index?: number
  ref?: React.Ref<IssueFormElementRef>
} & SharedElementProps) => {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment CheckboxesElement_input on IssueFormElementCheckboxes {
        __id
        itemId: id
        label
        descriptionHTML
        checkboxOptions: options {
          label
          labelHTML
          required
        }
      }
    `,
    elementRef,
  )

  if (!data.checkboxOptions) return null
  const elementStorageKey = uniqueIdForElementInternal(sessionStorageKey, '', data.label, data.__id)
  return (
    <CheckboxesElementInternal
      {...data}
      type={'checkboxes'}
      index={index}
      ref={ref}
      sessionStorageKey={elementStorageKey}
      defaultValuesById={defaultValuesById}
      onFieldChange={onFieldChange}
    />
  )
}

export const CheckboxesElementInternal = ({
  ref,
  itemId,
  index: indexOfFormElement,
  sessionStorageKey,
  label,
  descriptionHTML,
  checkboxOptions,
  defaultValuesById,
  onFieldChange,
}: CheckboxesElementInternalProps &
  SharedElementProps & {
    ref?: React.Ref<IssueFormElementRef>
  }) => {
  const firstCheckboxRef = useRef<HTMLInputElement>(null)

  const defaultCheckedValues = useMemo(() => {
    return getValidDefaultChoices({
      itemId,
      indexOfFormElement,
      defaultValuesById,
      checkboxCount: checkboxOptions.length,
    })
  }, [checkboxOptions.length, defaultValuesById, indexOfFormElement, itemId])

  const [selectedItems, setSelectedItems] = useSessionStorage<boolean[]>(sessionStorageKey, defaultCheckedValues)

  const latestSelectedItemsRef = useRef(selectedItems)

  // Keep ref updated with latest selectedItems
  useEffect(() => {
    latestSelectedItemsRef.current = selectedItems
  }, [selectedItems])

  // Updates selected items with new default values from defaultValuesById
  useEffect(() => {
    if (onFieldChange && defaultValuesById) {
      const defaultSelection = getValidDefaultChoices({
        itemId,
        indexOfFormElement,
        defaultValuesById,
        checkboxCount: checkboxOptions.length,
      })

      setSelectedItems(defaultSelection)
      latestSelectedItemsRef.current = defaultSelection
    }
  }, [defaultValuesById, indexOfFormElement, itemId, checkboxOptions.length, setSelectedItems, onFieldChange])

  // Sends out user changes to selectedItems using the ref for latest value
  useEffect(() => {
    if (onFieldChange) {
      const selectedItemsArray = latestSelectedItemsRef.current
      const defaultValuesArray = getValidDefaultChoices({
        itemId,
        indexOfFormElement,
        defaultValuesById,
        checkboxCount: checkboxOptions.length,
      })

      // Compare arrays element by element
      const arraysEqual =
        selectedItemsArray.length === defaultValuesArray.length &&
        selectedItemsArray.every((value, index) => value === defaultValuesArray[index])

      if (!arraysEqual) {
        onFieldChange(itemId, indexOfFormElement, selectedItemsArray.map(item => item.toString()).join(','))
      }
    }
  }, [selectedItems, itemId, indexOfFormElement, onFieldChange, defaultValuesById, checkboxOptions.length])

  const [validationResult, setValidationResult] = useState<string | undefined>(undefined)

  useImperativeHandle(
    ref,
    () => ({
      focus: () => firstCheckboxRef.current?.focus(),
      markdown: () => {
        const bodyContent = checkboxOptions
          .map((option, checkboxIndex) => prefixMarkdownCheckbox(option.label, selectedItems[checkboxIndex]!))
          .join('\n')

        return prefixMarkdownTitle(label, bodyContent)
      },
      validate: () => {
        for (const [checkboxIndex, checkbox] of checkboxOptions.entries()) {
          if (checkbox.required && !selectedItems[checkboxIndex]) {
            setValidationResult(VALIDATORS.checkboxInAGroupMustBeSelected)
            return false
          }
        }

        return true
      },
      reset: () => {
        setSelectedItems(defaultCheckedValues)
        setValidationResult(undefined)
      },
      hasChanges: () => !defaultCheckedValues.every((value, index) => value === selectedItems[index]),
      getSessionStorageKey: () => sessionStorageKey,
      type: 'checkboxes' as const,
    }),
    [checkboxOptions, defaultCheckedValues, label, selectedItems, sessionStorageKey, setSelectedItems],
  )

  useEffect(() => {
    // If we had a validation error and the user has fixed it, clear the error
    if (validationResult && checkboxOptions.every((option, index) => !option.required || selectedItems[index])) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setValidationResult(undefined)
    }
  }, [checkboxOptions, selectedItems, validationResult])

  return (
    <CheckboxGroup className={styles.CheckboxesElementGroup}>
      <CheckboxGroup.Label className={styles.CheckboxesElementGroupLabel}>{label}</CheckboxGroup.Label>
      {descriptionHTML && (
        <CheckboxGroup.Caption className={styles.CheckboxesElementGroupDescription}>
          <VerifiedHTMLBox html={descriptionHTML as SafeHTMLString} className="markdown-body note text-small mb-2" />
        </CheckboxGroup.Caption>
      )}
      {checkboxOptions.map((option, checkboxIndex) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <FormControl key={checkboxIndex} required={option.required ?? false}>
          <Checkbox
            ref={checkboxIndex === 0 ? firstCheckboxRef : undefined}
            checked={selectedItems[checkboxIndex]}
            onChange={() => {
              setSelectedItems(selectedItems.map((item, index) => (index === checkboxIndex ? !item : item)))
            }}
          />
          <FormControl.Label className={styles.checkboxOptionLabel}>
            <VerifiedHTMLText html={option.labelHTML as SafeHTMLString} />
          </FormControl.Label>
        </FormControl>
      ))}
      {validationResult && <CheckboxGroup.Validation variant="error">{validationResult}</CheckboxGroup.Validation>}
    </CheckboxGroup>
  )
}

CheckboxesElement.displayName = 'CheckboxesElement'
CheckboxesElementInternal.displayName = 'CheckboxesElementInternal'
